import type {
  SingleChoiceItem,
  RankingItem,
  MultipleChoiceItem,
  MatchingItem,
  KeyboardInputItem,
  ElementsTypes,
  TestGroupingGroup,
} from '@vedalib/editor/lib/elements'
import { CONFIG_ELEMENTS, SectionTypeEnum } from '@vedalib/editor/lib/elements'
import { rtValueToText } from '@vedalib/rich-text'
import { format, intervalToDuration } from 'date-fns'

import { getAllTextInBlock } from 'components/Editor/EditorContext/selectors/blockAnalytic'
import { LmsService } from 'services/lms/lms.service'
import type { ScormRuntimeVersion } from 'services/lms/transport/common'

import { getBlock, getBlockState, getElementState, getSections } from '../selectors'
import type { Block, IProjectContext } from '../types'

export type ITestsItemType =
  | SingleChoiceItem
  | RankingItem
  | MultipleChoiceItem
  | MatchingItem
  | KeyboardInputItem

type ParsedPairs = {
  correctAnswer: { fact: string; supplement: string }[]
  studentResponse: { fact: string; supplement: string }[]
  isSequencing?: boolean
  isChoiceMultiple?: boolean
  isFillIn?: boolean
}

export type ScormVersion = Exclude<ScormRuntimeVersion, 'none'>

type ScormQuestionType = 'matching' | 'sequencing' | 'choice' | 'fill-in' | 'other'
export const INTERACTIONS_ELEMENTS = [
  'testSingleChoice',
  'testMultipleChoice',
  'testRanking',
  'testMatching',
  'testKeyboardInput',
  'testFeedback',
  'testHotspot',
  'testGrouping',
]

// TODO: for correct work with all lms like scorm cmi.interaction.n... (n - index element) index need increment correctly (possibly store in suspend_data and added when need send data to LMS)) just now interactions work for particular client
export const getInteractiveElementIndex = (state: IProjectContext, elementId: string): number => {
  const sections = getSections(state)
  const interactiveElementSet = new Set<string>()
  sections.forEach((section) => {
    section.blocks.forEach((block) => {
      Object.entries(block.elements).forEach(([elId, el]) => {
        if (
          (section.type === SectionTypeEnum.test && INTERACTIONS_ELEMENTS.includes(el?.type)) ||
          el?.type === 'reflection'
        ) {
          interactiveElementSet.add(elId)
        }
      })
    })
  })
  const interactiveElementArray = Array.from(interactiveElementSet)
  return interactiveElementArray.indexOf(elementId)
}

const MATCHING_RESPONSE_SEPARATOR_SCORM_1_2 = '.'
const MULTIPLE_CHOICE_AND_SEQUENCING_RESPONSE_SEPARATOR_SCORM_1_2 = ','
const PATTERN_SEPARATOR_SCORM_1_2 = ','

const MATCHING_RESPONSE_SEPARATOR_SCORM_2004 = '[.]'
const MULTIPLE_CHOICE_AND_SEQUENCING_RESPONSE_SEPARATOR_SCORM_2004 = '[,]'
const PATTERN_SEPARATOR_SCORM_2004 = '[,]'
const TIME_FORMAT = 'HH:mm:ss'
const ID_SEPARATOR = '_'
const RESPONSE_SEPARATOR = '-'

const separatorMap = {
  '1.2': {
    matching: MATCHING_RESPONSE_SEPARATOR_SCORM_1_2,
    sequencing: MULTIPLE_CHOICE_AND_SEQUENCING_RESPONSE_SEPARATOR_SCORM_1_2,
    choice: MULTIPLE_CHOICE_AND_SEQUENCING_RESPONSE_SEPARATOR_SCORM_1_2,
    pattern: PATTERN_SEPARATOR_SCORM_1_2,
    'fill-in': '',
    other: '',
  },
  '2004': {
    matching: MATCHING_RESPONSE_SEPARATOR_SCORM_2004,
    sequencing: MULTIPLE_CHOICE_AND_SEQUENCING_RESPONSE_SEPARATOR_SCORM_2004,
    choice: MULTIPLE_CHOICE_AND_SEQUENCING_RESPONSE_SEPARATOR_SCORM_2004,
    pattern: PATTERN_SEPARATOR_SCORM_2004,
    'fill-in': '',
    other: '',
  },
}

const formatLatency = (milliseconds: number, version: ScormVersion) => {
  const { minutes = 0, seconds = 0 } = intervalToDuration({ start: 0, end: milliseconds })

  const totalHours = Math.floor(milliseconds / (1000 * 60 * 60))
  const ms = milliseconds % 1000

  if (version === '2004') {
    const floatSeconds = (seconds + ms / 1000).toFixed(2).replace(/\.00$/, '')
    const parts = []
    if (totalHours) {
      parts.push(`${totalHours}H`)
    }
    if (minutes) {
      parts.push(`${minutes}M`)
    }
    if (floatSeconds !== '0') {
      parts.push(`${floatSeconds}S`)
    }

    return `PT${parts.join('') || '0S'}`
  }

  const hours = String(totalHours).padStart(4, '0')
  const mins = String(minutes).padStart(2, '0')
  const secs = (seconds + ms / 1000).toFixed(2).padStart(5, '0')

  return `${hours}:${mins}:${secs}`
}

export const getInteractionTime = (version: ScormVersion) => {
  return version === '2004'
    ? { timestamp: format(new Date(), "yyyy-MM-dd'T'HH:mm:ss") }
    : { time: format(new Date(), TIME_FORMAT) }
}

const getLabelTestElement = (
  item?: ITestsItemType | string,
  property?: 'leftLabel' | 'rightLabel',
) => {
  if (typeof item === 'string') {
    return item
  }
  if (item && isMatchingItem(item) && property) {
    return rtValueToText(item[property] ?? '')
  }
  if (item && 'label' in item) {
    return rtValueToText(item.label ?? '')
  }
  return ''
}

const replaceAllSpecSymbolsOnSeparator = (str: string, separator = RESPONSE_SEPARATOR) =>
  str.replace(/[^\p{L}0-9]/gu, separator)

const generateScormMatchingPattern = (
  pairs: { fact: string; supplement: string }[],
  scormVersion: ScormVersion,
) => {
  const separator = separatorMap[scormVersion]['matching']
  const pattern = separatorMap[scormVersion]['pattern']
  const factMap = new Map<string, string>()
  const supplementMap = new Map<string, string>()
  // let factCounter = 0
  // let supplementCounter = 0

  // const getFactId = (fact: string) => {
  //   if (!factMap.has(fact)) {
  //     const id = String.fromCharCode(97 + factCounter) // a, b, c...
  //     factMap.set(fact, id)
  //     factCounter++
  //   }
  //   return factMap.get(fact)
  // }

  // const getSupplementId = (supplement: string) => {
  //   if (!supplementMap.has(supplement)) {
  //     const id = String(supplementCounter + 1) // 1, 2, 3...
  //     supplementMap.set(supplement, id)
  //     supplementCounter++
  //   }
  //   return supplementMap.get(supplement)
  // }

  // TODO:rude solution not main standard lms for particular client
  const getFactId = (fact: string) => {
    if (!factMap.has(fact)) {
      factMap.set(fact, fact)
    }
    return factMap.get(fact)
  }

  // TODO:rude solution not main standard lms for particular client
  const getSupplementId = (supplement: string) => {
    if (!supplementMap.has(supplement)) {
      supplementMap.set(supplement, supplement)
    }
    return supplementMap.get(supplement)
  }

  const patternParts = pairs.map(({ fact, supplement }) => {
    // const factId = getFactId(fact)
    // const supplementId = getSupplementId(supplement)
    // TODO:rude solution not main standard lms
    const factId = getFactId(replaceAllSpecSymbolsOnSeparator(fact))
    const supplementId = getSupplementId(replaceAllSpecSymbolsOnSeparator(supplement))
    return `${factId}${separator}${supplementId}`
  })

  return {
    pattern: patternParts.join(pattern),
    factMap: Object.fromEntries(factMap),
    supplementMap: Object.fromEntries(supplementMap),
  }
}

const parseQuestionItemsToPairs = (
  questionItems: ITestsItemType[],
  element: Record<string, unknown>,
  type: ScormQuestionType = 'other',
) => {
  let val: string[] = []
  if (Array.isArray(element?.value) && element.value.every((v) => typeof v === 'string')) {
    val = element.value
  }
  const studentResponse = val.map((item) => questionItems.find((i) => i.value === item))

  switch (type) {
    case 'matching':
      return {
        correctAnswer: questionItems.map((item) => ({
          fact: getLabelTestElement(item, 'leftLabel'),
          supplement: getLabelTestElement(item, 'rightLabel'),
        })),
        studentResponse: studentResponse.map((item) => ({
          fact: getLabelTestElement(item, 'leftLabel'),
          supplement: getLabelTestElement(item, 'rightLabel'),
        })),
      }

    case 'sequencing':
      return {
        correctAnswer: questionItems.map((item) => ({
          fact: getLabelTestElement(item),
          supplement: '',
        })),
        studentResponse: studentResponse.map((item) => ({
          fact: getLabelTestElement(item),
          supplement: '',
        })),
      }

    case 'choice':
      const correctAnswer = questionItems.filter((item) =>
        'isValid' in item ? item.isValid : true,
      )
      return {
        correctAnswer: correctAnswer.map((item) => ({
          fact: getLabelTestElement(item),
          supplement: '',
        })),
        studentResponse: studentResponse.map((item) => ({
          fact: getLabelTestElement(item),
          supplement: '',
        })),
      }

    case 'fill-in':
      return {
        correctAnswer: questionItems.map((item) => ({
          fact: getLabelTestElement(item),
          supplement: '',
        })),
        studentResponse: [element.rawValue].map((item) => ({
          fact: getLabelTestElement(typeof item === 'string' ? item : ''),
          supplement: '',
        })),
      }

    default:
      return {
        correctAnswer: questionItems.map((item) => ({
          fact: getLabelTestElement(item),
          supplement: '',
        })),
        studentResponse: studentResponse.map((item) => ({
          fact: getLabelTestElement(item),
          supplement: '',
        })),
      }
  }
}

const generateScormPattern = (
  pairs: ParsedPairs,
  type: ScormQuestionType,
  scormVersion: ScormVersion,
) => {
  switch (type) {
    case 'fill-in':
      return {
        correctAnswer: pairs.correctAnswer.map((p) => p.fact)[0] || '',
        studentResponse: pairs.studentResponse.map((p) => p.fact)[0] || '',
      }

    case 'sequencing':
    case 'choice': {
      const separator = separatorMap[scormVersion]['sequencing']
      const map = new Map<string, string>()
      // let counter = 0

      // const getId = (fact: string) => {
      //   if (!map.has(fact)) {
      //      map.set(fact, String.fromCharCode(97 + counter)) // a, b, c...
      //      counter++
      //   }
      //   return map.get(fact)!
      // }

      // TODO:rude solution not main standard lms
      const getId = (fact: string) => {
        const preparedFact = replaceAllSpecSymbolsOnSeparator(fact)
        if (!map.has(preparedFact)) {
          map.set(preparedFact, preparedFact)
        }
        return map.get(preparedFact)
      }

      return {
        correctAnswer: pairs.correctAnswer.map((p) => getId(p.fact)).join(separator),
        studentResponse: pairs.studentResponse.map((p) => getId(p.fact)).join(separator),
      }
    }

    case 'matching': {
      const res = generateScormMatchingPattern(pairs.correctAnswer, scormVersion)
      return {
        correctAnswer: res.pattern,
        studentResponse: encodeStudentResponseWithMaps(
          pairs.studentResponse,
          {
            factMap: res.factMap,
            supplementMap: res.supplementMap,
          },
          scormVersion,
        ),
      }
    }

    default:
      return {
        correctAnswer: '',
        studentResponse: '',
      }
  }
}

const encodeStudentResponseWithMaps = (
  studentResponse: { fact: string; supplement: string }[],
  maps: {
    factMap: Record<string, string>
    supplementMap: Record<string, string>
  },
  scormVersion: ScormVersion,
) => {
  const separator = separatorMap[scormVersion]['matching']
  const patternSeparator = separatorMap[scormVersion]['pattern']
  return studentResponse
    .map(({ fact, supplement }) => {
      // const factId = maps.factMap[fact]
      // const supplementId = maps.supplementMap[supplement]
      // TODO:rude solution not main standard lms
      const factId = maps.factMap[replaceAllSpecSymbolsOnSeparator(fact)]
      const supplementId = maps.supplementMap[replaceAllSpecSymbolsOnSeparator(supplement)]
      return `${factId}${separator}${supplementId}`
    })
    .join(patternSeparator)
}

const ScormQuestionTypeMap = {
  [CONFIG_ELEMENTS.testKeyboardInput.type]: 'fill-in',
  [CONFIG_ELEMENTS.testMatching.type]: 'matching',
  [CONFIG_ELEMENTS.testMultipleChoice.type]: 'choice',
  [CONFIG_ELEMENTS.testRanking.type]: 'sequencing',
  [CONFIG_ELEMENTS.testSingleChoice.type]: 'choice',
  [CONFIG_ELEMENTS.testGrouping.type]: 'matching',
  other: 'other',
} as const

const ScormQuestionResultSCORM_1_2Map = {
  correct: 'correct',
  incorrect: 'wrong',
  unanticipated: 'neutral',
} as const

type ConfigQuestionType = keyof typeof ScormQuestionTypeMap

const isScormQuestionType = (type: string): type is ConfigQuestionType => {
  return type in ScormQuestionTypeMap
}

const isMatchingItem = (item: ITestsItemType): item is MatchingItem => {
  return 'leftLabel' in item && 'rightLabel' in item
}

const NONE_GROUP_LABEL = 'None'

const parseGroupingToPairs = (
  groups: TestGroupingGroup[],
  destination: TestGroupingGroup[] | undefined,
): ParsedPairs => {
  const correctAnswer: { fact: string; supplement: string }[] = []
  const studentResponse: { fact: string; supplement: string }[] = []

  groups.forEach((group) => {
    const groupLabel = rtValueToText(group.target.label ?? '')
    ;(group.items || []).forEach((item) => {
      const answerLabel = rtValueToText(item.label ?? '')
      correctAnswer.push({
        fact: groupLabel,
        supplement: answerLabel,
      })
    })
  })

  if (destination) {
    const placedAnswers = new Set<string>()
    destination.forEach((group) => {
      const groupLabel = rtValueToText(group.target.label ?? '')
      ;(group.items || []).forEach((item) => {
        const answerLabel = rtValueToText(item.label ?? '')
        studentResponse.push({
          fact: groupLabel,
          supplement: answerLabel,
        })
        placedAnswers.add(item.value)
      })
    })

    groups.forEach((group) => {
      ;(group.items || []).forEach((item) => {
        if (!placedAnswers.has(item.value)) {
          const answerLabel = rtValueToText(item.label ?? '')
          studentResponse.push({
            fact: NONE_GROUP_LABEL,
            supplement: answerLabel,
          })
        }
      })
    })
  }

  return { correctAnswer, studentResponse }
}

const getScormQuestionType = (type: string) =>
  isScormQuestionType(type) ? ScormQuestionTypeMap[type] : ScormQuestionTypeMap['other']

const getQuestionResult = (isValid?: boolean) => {
  if (isValid) {
    return 'correct'
  }

  if (isValid === false) {
    return 'incorrect'
  }

  return 'unanticipated'
}

const getInformativeId = ({
  block,
  questionType,
  sectionIndex,
  index,
}: {
  block?: Block | null
  questionType: ElementsTypes
  sectionIndex: number
  index: number
}) => {
  const allTextInQuestionBlock = getAllTextInBlock({ block, separator: ID_SEPARATOR })

  const preparedText = replaceAllSpecSymbolsOnSeparator(allTextInQuestionBlock, ID_SEPARATOR)

  return `${preparedText}${ID_SEPARATOR}${questionType}${ID_SEPARATOR}${sectionIndex}${ID_SEPARATOR}${index}`
}

export const getInteractiveQuestionElementId = (state: IProjectContext, blockId: string) => {
  const elements = state?.data?.blocks?.[blockId]?.elements
  const stateElementIds = Object.keys(elements).filter((elId) =>
    INTERACTIONS_ELEMENTS.includes(elements[elId]?.type),
  )
  return Object.keys(state?.state?.elements)?.find((elId) => stateElementIds.includes(elId))
}

export const getInteractiveQuestionType = (state: IProjectContext, blockId: string) => {
  const questionElementId = getInteractiveQuestionElementId(state, blockId) || ''
  return state.data.blocks[blockId]?.elements[questionElementId]?.type
}

export const setInteractiveScorm = ({
  state,
  blockId,
}: {
  state: IProjectContext
  blockId: string
}) => {
  const lms = LmsService.instance
  const scormVersion = lms.getScormVersion()
  const version = scormVersion === 'none' ? '1.2' : scormVersion
  const testBlock = getBlockState(state)
  const sections = getSections(state)
  const block = getBlock(state, { id: blockId })
  const sectionIndex = sections.findIndex((section) =>
    section.blocks?.some((b) => b.uuid === blockId),
  )
  const questionElementId = getInteractiveQuestionElementId(state, blockId)
  if (!questionElementId) {
    console.warn(`[SCORM] No interaction elements found in block ${blockId}`)
    return
  }
  const result = getQuestionResult(testBlock?.result?.isValid)

  const blockData = state.state.blocks[blockId]
  const index = getInteractiveElementIndex(state, questionElementId)

  if (index >= 0) {
    const questionElement = state.data.blocks[blockId]?.elements[questionElementId]
    const questionType = questionElement?.type
    const questionScormType = getScormQuestionType(questionType)
    const element = state.state.elements?.[questionElementId]

    let pairsAnswer: ParsedPairs

    if (questionType === CONFIG_ELEMENTS.testGrouping.type) {
      const groups = (questionElement.value.items as TestGroupingGroup[]) || []
      const destination = (element as { destination?: TestGroupingGroup[] })?.destination
      pairsAnswer = parseGroupingToPairs(groups, destination)
    } else {
      const questionItems = (questionElement.value.items as ITestsItemType[]) || []
      pairsAnswer = parseQuestionItemsToPairs(questionItems, element, questionScormType)
    }

    // TODO: think about rewrite, try test element validate through questionValidate.ts
    const { correctAnswer, studentResponse } = generateScormPattern(
      pairsAnswer,
      questionScormType,
      version,
    )
    lms.setInteraction(index, {
      id: getInformativeId({ block, questionType, sectionIndex, index }),
      type: questionScormType,
      ...(scormVersion === '2004' && { learner_response: studentResponse }),
      ...(scormVersion === '1.2' && { student_response: studentResponse }),
      correctAnswer,
      result: scormVersion === '1.2' ? ScormQuestionResultSCORM_1_2Map[result] : result,
      latency: formatLatency(Date.now() - (blockData?.startTime ?? 0), version),
      ...getInteractionTime(version),
    })
  }
}

//TODO: refactor after 628-us-bug-student-response-v-scorm-12 merge
export const setReflectionScorm = ({
  state,
  elementId,
  questionText,
}: {
  state: IProjectContext
  elementId?: string
  questionText?: string
}) => {
  if (!elementId) {
    return
  }
  const lms = LmsService.instance
  const scormVersion = lms.getScormVersion()
  const version = scormVersion === 'none' ? '1.2' : scormVersion
  const value = getElementState(state, elementId)?.value
  const trimmedValue = typeof value === 'string' ? value : ''
  let responseField = 'learner_response'
  let finalValue = trimmedValue
  if (version === '1.2') {
    finalValue = finalValue.slice(0, 255)
    responseField = 'student_response'
  } else {
    finalValue = finalValue.slice(0, 4000)
    responseField = 'learner_response'
  }
  const index = getInteractiveElementIndex(state, elementId)
  lms.setInteraction(index, {
    id:
      version === '1.2'
        ? `reflection_${elementId || ''}:${questionText}`
        : `reflection_${elementId || ''}`,
    type: 'fill-in',
    description: version === '1.2' ? undefined : questionText,
    [responseField]: finalValue,
    result: 'neutral',
    ...(scormVersion !== 'none' && getInteractionTime(version)),
    correctAnswer: '',
    latency: '',
  })
}
